/**
 * tool-coordination.ts
 *
 * Centralized tool dispatching, artifact identifier normalization,
 * and post-execution validity guards for Gemini Live agents.
 */

export interface NormalizedArtifactId {
  raw: string;
  trimmed: string;
  isInvalid: boolean;
  isCurrent: boolean;
  isUuid: boolean;
  normalizedUuid?: string;
  searchTerm?: string;
}

export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Normalizes a model identifier string for comparison.
 */
export function normalizeModelId(modelId: string | null | undefined): string | null {
  if (typeof modelId !== 'string') return null;
  const trimmed = modelId.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/**
 * Parses and normalizes an artifact_id parameter consistently across handlers and guards.
 *
 * Rules:
 * - undefined, "", whitespace-only, and case-insensitive "current" (with optional whitespace) -> isCurrent: true
 * - Malicious input (__proto__, constructor, prototype, debug:*) -> isInvalid: true
 * - UUID strings -> isUuid: true, normalizedUuid: lowercase trimmed
 * - Non-UUID strings -> searchTerm: trimmed with ORIGINAL casing preserved
 */
export function parseAndNormalizeArtifactId(artifactId: unknown): NormalizedArtifactId {
  const raw = typeof artifactId === 'string' ? artifactId : '';
  const trimmed = raw.trim();

  // Input validation: reject prototype pollution or malicious debug injection
  if (
    trimmed.includes('__proto__') ||
    trimmed.includes('constructor') ||
    trimmed.includes('prototype') ||
    trimmed.startsWith('debug:')
  ) {
    return {
      raw,
      trimmed,
      isInvalid: true,
      isCurrent: false,
      isUuid: false,
    };
  }

  // Missing, empty string, all whitespace, or case-insensitive "current"
  if (!trimmed || trimmed.toLowerCase() === 'current') {
    return {
      raw,
      trimmed,
      isInvalid: false,
      isCurrent: true,
      isUuid: false,
    };
  }

  // UUID (case-insensitive, trimmed)
  if (UUID_REGEX.test(trimmed)) {
    return {
      raw,
      trimmed,
      isInvalid: false,
      isCurrent: false,
      isUuid: true,
      normalizedUuid: trimmed.toLowerCase(),
    };
  }

  // Non-UUID string: artifact name for search. Preserves original casing.
  return {
    raw,
    trimmed,
    isInvalid: false,
    isCurrent: false,
    isUuid: false,
    searchTerm: trimmed,
  };
}

/**
 * Determines whether a requested artifact_id is targeting the currently active model.
 */
export function isTargetingCurrentArtifact(
  artifactId: unknown,
  activeModelId: string | null | undefined
): boolean {
  const parsed = parseAndNormalizeArtifactId(artifactId);
  if (parsed.isInvalid) return false;
  if (parsed.isCurrent) return true;

  const normalizedActive = normalizeModelId(activeModelId);
  if (normalizedActive) {
    const candidate = parsed.normalizedUuid || parsed.trimmed.toLowerCase();
    return candidate === normalizedActive;
  }

  return false;
}

/**
 * Constructs a function call replacement response when the viewed 3D model
 * changes or unmounts during asynchronous dossier retrieval.
 */
export function createStaleModelResponse(
  fc: { id: string; name: string },
  previousModelId: string | null,
  currentModelId: string | null
) {
  return {
    id: fc.id,
    name: fc.name,
    response: {
      output: {
        success: false,
        error: `Mô hình hiện vật đang xem đã thay đổi hoặc đã đóng trong khi đang tra cứu (từ ${previousModelId || 'không'} sang ${currentModelId || 'đóng'}). Vui lòng phản hồi dựa trên hiện vật đang hiển thị hiện tại.`,
        modelChanged: true,
        previousModelId: previousModelId,
        currentModelId: currentModelId,
      },
    },
  };
}

export interface GuardToolResponsesParams {
  functionCalls: Array<{ id: string; name: string; args?: any }>;
  rawResponses: any[];
  initialModelId: string | null;
  initialViewerMounted: boolean;
  currentModelId: string | null;
  currentViewerMounted: boolean;
}

/**
 * Re-validates dossier results post-execution (after Promise.all) immediately before sendToolResponse.
 * If the active model or viewer mounted state changed during execution,
 * any dossier result targeting the initial model is replaced with a stale response.
 * Unrelated tool results and queries for explicitly specified other artifacts are kept intact.
 * Camera rotation on the same model does not invalidate the response.
 */
export function guardToolResponsesPostExecution({
  functionCalls,
  rawResponses,
  initialModelId,
  initialViewerMounted,
  currentModelId,
  currentViewerMounted,
}: GuardToolResponsesParams): any[] {
  const normalizedInitialModel = normalizeModelId(initialModelId);
  const normalizedCurrentModel = normalizeModelId(currentModelId);

  const modelOrViewerChanged =
    normalizedCurrentModel !== normalizedInitialModel ||
    currentViewerMounted !== initialViewerMounted;

  return rawResponses.map((res, index) => {
    const fc = functionCalls[index];
    if (!fc || fc.name !== 'get_artifact_details') {
      return res;
    }

    const isTargetingCurrent = isTargetingCurrentArtifact(
      fc.args?.artifact_id,
      initialModelId
    );

    if (isTargetingCurrent && modelOrViewerChanged) {
      console.warn(
        `[ToolCoordination] Model or viewer changed while resolving ${fc.name}: was ${initialModelId}, now ${currentModelId}`
      );
      return createStaleModelResponse(fc, initialModelId, currentModelId);
    }

    return res;
  });
}

export interface FunctionCallItem {
  id: string;
  name: string;
  args?: any;
}

export interface ExecuteAndCoordinateToolCallsParams {
  functionCalls: FunctionCallItem[];
  client: any;
  getCurrentClient: () => any;
  toolRegistry: Record<string, any>;
  handleToolCall: (fc: any, tools: Record<string, any>) => Promise<any>;
  getActiveModelId: () => string | null;
  hasModelViewerMounted: () => boolean;
}

/**
 * Executes a batch of function calls concurrently, guards against session changes,
 * re-checks artifact validity post-execution, logs diagnostics, and sends responses.
 */
export async function executeAndCoordinateToolCalls({
  functionCalls,
  client,
  getCurrentClient,
  toolRegistry,
  handleToolCall,
  getActiveModelId,
  hasModelViewerMounted,
}: ExecuteAndCoordinateToolCallsParams): Promise<any[] | null> {
  const callClient = client;
  const callSession = (client as any)?.session;
  const initialModelId = getActiveModelId();
  const initialViewerMounted = hasModelViewerMounted();

  // 1. Run all tool calls concurrently
  const rawResponses = await Promise.all(
    functionCalls.map((fc) => handleToolCall(fc, toolRegistry))
  );

  // 2. Guard against session disconnection or reconnection while awaiting tool call
  const currentClient = getCurrentClient();
  if (
    !currentClient ||
    currentClient !== callClient ||
    currentClient.status !== 'connected' ||
    (callSession && (currentClient as any)?.session !== callSession)
  ) {
    console.warn(
      '[ToolCoordination] Discarding tool response: session or connection changed during execution'
    );
    return null;
  }

  // 3. Post-execution guard: check model/viewer validity SAU Promise.all, right before sendToolResponse
  const currentModelId = getActiveModelId();
  const currentViewerMounted = hasModelViewerMounted();

  const finalResponses = guardToolResponsesPostExecution({
    functionCalls,
    rawResponses,
    initialModelId,
    initialViewerMounted,
    currentModelId,
    currentViewerMounted,
  });

  // 4. Record to diagnostic log if window is defined
  if (typeof window !== 'undefined') {
    (window as any).__lastToolCalls = (window as any).__lastToolCalls || [];
    for (let i = 0; i < functionCalls.length; i++) {
      (window as any).__lastToolCalls.push({
        id: functionCalls[i].id,
        name: functionCalls[i].name,
        args: functionCalls[i].args,
        response: finalResponses[i],
        timestamp: Date.now(),
      });
    }
  }

  // 5. Send tool response to Gemini client
  callClient.sendToolResponse({
    functionResponses: finalResponses,
  });

  return finalResponses;
}
